import React, { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { staffDataAtom, themeAtom } from "../../../../-Atoms";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { db, updateDoc, doc, collection, where, query, getDocs } from "../../../../firebaseConfig";
const { DateTime } = require("luxon");
const dayjs = require("dayjs");

const StaffSelector = ({
  label,
  value,
  onChange,
  jobData,
  fieldName,
  setHasUnsavedChanges,
  disabled = false,
  assignedStaff = [],
  customChangeHandler = null,
  isSpecialEvent,
}) => {
  const [theme] = useAtom(themeAtom);
  const [staffData] = useAtom(staffDataAtom);
  const [externalDialogOpen, setExternalDialogOpen] = useState(false);
  const [externalName, setExternalName] = useState("External");

  const checkWWCC = (option) => {
    let exists = true;
    let inDate = true;
    if (!option?.wwccCheckNumber) exists = false;
    if (!option?.wwccExpiry) exists = false;
    if (!option?.wwccExpiry) {
      inDate = false;
    } else {
      if (
        DateTime.fromISO(option?.wwccExpiry)
          .setZone("Australia/Sydney")
          .startOf("day") <=
        DateTime.now().setZone("Australia/Sydney").startOf("day")
      ) {
        inDate = false;
      } else {
        inDate = true;
      }
    }
    return { exists, inDate };
  };

  const handleStaffChange = (event, newValue, reason) => {
    if (disabled) return;

    // Handle External option
    if (newValue === "External") {
      setExternalDialogOpen(true);
      return;
    }

    let staffId = "";
    if (newValue?.id) staffId = newValue.id;
    if (customChangeHandler) {
      customChangeHandler(staffId);
      return;
    }
    onChange(staffId);
    const updateData = {};
    updateData[fieldName] = staffId;
    updateDoc(doc(db, "JobData", jobData.id), updateData)
      .then(() => {
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      })
      .catch((error) => {
        console.error("Error updating staff:", error);
      });
  };

  const handleExternalSave = () => {
    const externalData = {
      type: "external",
      name: externalName,
    };

    if (customChangeHandler) {
      customChangeHandler(externalData);
    } else {
      onChange(externalData);
      const updateData = {};
      updateData[fieldName] = externalData;
      updateDoc(doc(db, "JobData", jobData.id), updateData)
        .then(() => {
          if (setHasUnsavedChanges) {
            setHasUnsavedChanges(false);
          }
        })
        .catch((error) => {
          console.error("Error updating external staff:", error);
        });
    }

    setExternalDialogOpen(false);
  };

  const handleExternalCancel = () => {
    setExternalName("External");
    setExternalDialogOpen(false);
  };

  const getDisplayValue = () => {
    if (value?.type === "external") {
      return { firstName: value.name, lastName: "", isExternal: true };
    }
    return staffData.find((staff) => staff.id === value) || null;
  };

  const getAutocompleteValue = () => {
    if (value?.type === "external") {
      return "External";
    }
    return staffData.find((staff) => staff.id === value) || null;
  };

  const getOptionLabel = (option) => {
    if (!option) return "";
    if (option === "External") return "External";
    if (option.divider) return "";
    if (typeof option === "string") return option;
    if (option.preferredName) {
      return `${option.firstName} (${option.preferredName}) ${option.lastName}`;
    }
    return `${option.firstName} ${option.lastName}`;
  };

  const filterOptions = createFilterOptions({
    stringify: (option) => {
      if (!option) return "";
      if (option === "External") return "External";
      if (option.divider) return "";
      if (typeof option === "string") return option;
      // Include firstName, lastName, and preferredName in the search
      return `${option.firstName} ${option.lastName} ${option.preferredName || ""}`;
    },
  });

  const selectedStaff = getDisplayValue();
  const isAlreadyAssigned =
    value && typeof value === "string" ? assignedStaff.includes(value) : false;

  return (
    <FormControl
      fullWidth
      sx={{
        position: "relative",
      }}
      variant="filled"
    >
      <Autocomplete
        disablePortal
        autoHighlight
        openOnFocus
        fullWidth
        filterSelectedOptions={false}
        filterOptions={filterOptions}
        options={[
          "External",
          { divider: true },
          ...staffData.filter(
            (staff) => staff.currentlyEmployed || staff.id === value
          ),
        ]}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={(option) => {
          if (option === "External") return false;
          if (option.divider) return true;
          return !option.currentlyEmployed;
        }}
        isOptionEqualToValue={(option, value) => {
          if (option === "External" && value === "External") return true;
          if (option?.id && value?.id) return option.id === value.id;
          return false;
        }}
        renderOption={(props, option) => {
          if (option === "External") {
            return (
              <Box
                component="li"
                {...props}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: "bold",
                  color: theme === "dark" ? "#4DAAE9" : "#1976d2",
                }}
              >
                External
              </Box>
            );
          }

          if (option.divider) {
            return (
              <Box key="divider" sx={{ padding: 0 }}>
                <Divider />
              </Box>
            );
          }

          const { exists, inDate } = checkWWCC(option);
          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                opacity: option.currentlyEmployed ? 1 : 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{ height: 28, width: 28, mr: 1 }}
                  alt={`${option.firstName} ${option.lastName}`}
                  src={option.avatar || ""}
                />
                <span
                  style={{
                    color:
                      assignedStaff.includes(option.id) && option.id !== value
                        ? "#d94141"
                        : "inherit",
                  }}
                >
                  {option.preferredName
                    ? `${option.firstName} (${option.preferredName}) ${option.lastName}`
                    : `${option.firstName} ${option.lastName}`}
                  {assignedStaff.includes(option.id) && option.id !== value
                    ? " (Already Assigned)"
                    : ""}
                </span>
              </Box>
              {exists && (
                <Chip
                  label={`${!inDate ? "!! " : ""}WWCC${
                    !inDate ? " !!" : ""
                  }`}
                  size="small"
                  sx={{
                    backgroundColor: inDate ? "#4DAAE9" : "#d94141",
                    color: "white",
                    fontSize: "0.6rem",
                    fontWeight: "bold",
                    height: 20,
                    ml: 1,
                  }}
                />
              )}
            </Box>
          );
        }}
        renderInput={(params) => {
          const showAvatar =
            selectedStaff &&
            params.inputProps.value === getOptionLabel(selectedStaff);
          return (
            <TextField
              {...params}
              label={label}
              variant="filled"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                startAdornment: showAvatar ? (
                  <Avatar
                    sx={{ height: 28, width: 28, mr: 1 }}
                    alt={
                      selectedStaff.isExternal
                        ? selectedStaff.firstName || "External"
                        : `${selectedStaff.firstName || ""} ${
                            selectedStaff.lastName || ""
                          }`
                    }
                    src={
                      selectedStaff.isExternal
                        ? ""
                        : selectedStaff.avatar || ""
                    }
                  >
                    {selectedStaff.isExternal ? "E" : ""}
                  </Avatar>
                ) : null,
                sx: {
                  color:
                    isAlreadyAssigned && value !== jobData[fieldName]
                      ? "#d94141"
                      : "inherit",
                },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "10px",
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(35,35,35,0.7)"
                      : "rgba(235,235,235,0.9)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                      : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                },
              }}
            />
          );
        }}
        value={getAutocompleteValue()}
        onChange={handleStaffChange}
        disabled={disabled}
        noOptionsText="No staff found"
      />

      <Dialog open={externalDialogOpen} onClose={handleExternalCancel}>
        <DialogTitle>External Staff Member</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExternalCancel}>Cancel</Button>
          <Button onClick={handleExternalSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </FormControl>
  );
};

const StaffContainer = ({
  jobData,
  isEventTomorrow,
  setHasUnsavedChanges,
  isSpecialEvent,
}) => {
  const [theme] = useAtom(themeAtom);
  const [photographer, setPhotographer] = useState(jobData?.photographer || "");
  const [operator, setOperator] = useState(jobData?.operator || "");
  const [stylist, setStylist] = useState(jobData?.stylist || "");
  const [additionalPhotographers, setAdditionalPhotographers] = useState(
    jobData?.additionalPhotographers || []
  );
  const [additionalOperators, setAdditionalOperators] = useState(
    jobData?.additionalOperators || []
  );
  const [additionalStylists, setAdditionalStylists] = useState(
    jobData?.additionalStylists || []
  );
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [additionalDates, setAdditionalDates] = useState(
    jobData?.additionalDates || []
  );

  useEffect(() => {
    setPhotographer(jobData?.photographer || "");
    setOperator(jobData?.operator || "");
    setStylist(jobData?.stylist || "");
    setAdditionalPhotographers(jobData?.additionalPhotographers || []);
    setAdditionalOperators(jobData?.additionalOperators || []);
    setAdditionalStylists(jobData?.additionalStylists || []);
    setAdditionalDates(jobData?.additionalDates || []);
  }, [jobData]);

  const getStaffOnDay = async () => {
    const startDate = DateTime.fromISO(jobData.bookedStart)
      .setZone("Australia/Sydney")
      .startOf("day")
      .toUTC()
      .toISO();
    const endDate = DateTime.fromISO(jobData.bookedFinish)
      .setZone("Australia/Sydney")
      .endOf("day")
      .toUTC()
      .toISO();

    const firestoreQuery = query(
      collection(db, "JobData"),
      where("bookedStart", ">=", startDate),
      where("bookedStart", "<=", endDate)
    );

    const snapshot = await getDocs(firestoreQuery);
    const staffOnDay = new Set();

    snapshot.forEach((doc) => {
      const job = doc.data();
      if (job.photographer) staffOnDay.add(job.photographer);
      if (job.operator) staffOnDay.add(job.operator);
      if (job.stylist) staffOnDay.add(job.stylist);
      if (job.additionalPhotographers) {
        job.additionalPhotographers.forEach((id) => staffOnDay.add(id));
      }
      if (job.additionalOperators) {
        job.additionalOperators.forEach((id) => staffOnDay.add(id));
      }
      if (job.additionalStylists) {
        job.additionalStylists.forEach((id) => staffOnDay.add(id));
      }
    });

    return Array.from(staffOnDay);
  };

  useEffect(() => {
    getStaffOnDay().then((staffOnDay) => {
      setAssignedStaff(staffOnDay);
    });
  }, [jobData]);

  const addAdditionalStaff = (type, dateIndex = -1) => {
    let updateData = {};
    if (dateIndex === -1) {
      if (type === "photographer") {
        const newAdditionalPhotographers = [...additionalPhotographers, ""];
        setAdditionalPhotographers(newAdditionalPhotographers);
        updateData = { additionalPhotographers: newAdditionalPhotographers };
      } else if (type === "operator") {
        const newAdditionalOperators = [...additionalOperators, ""];
        setAdditionalOperators(newAdditionalOperators);
        updateData = { additionalOperators: newAdditionalOperators };
      } else if (type === "stylist") {
        const newAdditionalStylists = [...additionalStylists, ""];
        setAdditionalStylists(newAdditionalStylists);
        updateData = { additionalStylists: newAdditionalStylists };
      }
    } else {
      const newAdditionalDates = [...additionalDates];
      if (type === "additionalPhotographers") {
        if (!newAdditionalDates[dateIndex].additionalPhotographers) {
          newAdditionalDates[dateIndex].additionalPhotographers = [];
        }
        newAdditionalDates[dateIndex].additionalPhotographers.push("");
      } else if (type === "additionalOperators") {
        if (!newAdditionalDates[dateIndex].additionalOperators) {
          newAdditionalDates[dateIndex].additionalOperators = [];
        }
        newAdditionalDates[dateIndex].additionalOperators.push("");
      } else if (type === "additionalStylists") {
        if (!newAdditionalDates[dateIndex].additionalStylists) {
          newAdditionalDates[dateIndex].additionalStylists = [];
        }
        newAdditionalDates[dateIndex].additionalStylists.push("");
      }
      setAdditionalDates(newAdditionalDates);
      updateData = { additionalDates: newAdditionalDates };
    }

    updateDoc(doc(db, "JobData", jobData.id), updateData)
      .catch((error) => {
        console.error("Error updating additional staff:", error);
      });
  };

  const removeAdditionalStaff = (type, index, dateIndex = -1) => {
    let updateData = {};
    if (dateIndex === -1) {
      if (type === "photographer") {
        const newAdditionalPhotographers = [...additionalPhotographers];
        newAdditionalPhotographers.splice(index, 1);
        setAdditionalPhotographers(newAdditionalPhotographers);
        updateData = { additionalPhotographers: newAdditionalPhotographers };
      } else if (type === "operator") {
        const newAdditionalOperators = [...additionalOperators];
        newAdditionalOperators.splice(index, 1);
        setAdditionalOperators(newAdditionalOperators);
        updateData = { additionalOperators: newAdditionalOperators };
      } else if (type === "stylist") {
        const newAdditionalStylists = [...additionalStylists];
        newAdditionalStylists.splice(index, 1);
        setAdditionalStylists(newAdditionalStylists);
        updateData = { additionalStylists: newAdditionalStylists };
      }
    } else {
      const newAdditionalDates = [...additionalDates];
      if (type === "additionalPhotographers") {
        if (newAdditionalDates[dateIndex].additionalPhotographers) {
          newAdditionalDates[dateIndex].additionalPhotographers.splice(
            index,
            1
          );
        }
      } else if (type === "additionalOperators") {
        if (newAdditionalDates[dateIndex].additionalOperators) {
          newAdditionalDates[dateIndex].additionalOperators.splice(index, 1);
        }
      } else if (type === "additionalStylists") {
        if (newAdditionalDates[dateIndex].additionalStylists) {
          newAdditionalDates[dateIndex].additionalStylists.splice(index, 1);
        }
      }
      setAdditionalDates(newAdditionalDates);
      updateData = { additionalDates: newAdditionalDates };
    }

    updateDoc(doc(db, "JobData", jobData.id), updateData)
      .catch((error) => {
        console.error("Error removing additional staff:", error);
      });
  };

  const handleAdditionalStaffChange = (type, index, value, dateIndex = -1) => {
    let updateData = {};
    if (dateIndex === -1) {
      if (type === "photographer") {
        const newAdditionalPhotographers = [...additionalPhotographers];
        newAdditionalPhotographers[index] = value;
        setAdditionalPhotographers(newAdditionalPhotographers);
        updateData = { additionalPhotographers: newAdditionalPhotographers };
      } else if (type === "operator") {
        const newAdditionalOperators = [...additionalOperators];
        newAdditionalOperators[index] = value;
        setAdditionalOperators(newAdditionalOperators);
        updateData = { additionalOperators: newAdditionalOperators };
      } else if (type === "stylist") {
        const newAdditionalStylists = [...additionalStylists];
        newAdditionalStylists[index] = value;
        setAdditionalStylists(newAdditionalStylists);
        updateData = { additionalStylists: newAdditionalStylists };
      }
    } else {
      const newAdditionalDates = [...additionalDates];
      if (type === "photographer") {
        newAdditionalDates[dateIndex].photographer = value;
      } else if (type === "operator") {
        newAdditionalDates[dateIndex].operator = value;
      } else if (type === "stylist") {
        newAdditionalDates[dateIndex].stylist = value;
      } else if (type === "additionalPhotographers") {
        if (!newAdditionalDates[dateIndex].additionalPhotographers) {
          newAdditionalDates[dateIndex].additionalPhotographers = [];
        }
        newAdditionalDates[dateIndex].additionalPhotographers[index] = value;
      } else if (type === "additionalOperators") {
        if (!newAdditionalDates[dateIndex].additionalOperators) {
          newAdditionalDates[dateIndex].additionalOperators = [];
        }
        newAdditionalDates[dateIndex].additionalOperators[index] = value;
      } else if (type === "additionalStylists") {
        if (!newAdditionalDates[dateIndex].additionalStylists) {
          newAdditionalDates[dateIndex].additionalStylists = [];
        }
        newAdditionalDates[dateIndex].additionalStylists[index] = value;
      }
      setAdditionalDates(newAdditionalDates);
      updateData = { additionalDates: newAdditionalDates };
    }

    updateDoc(doc(db, "JobData", jobData.id), updateData)
      .then(() => {
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      })
      .catch((error) => {
        console.error("Error updating additional staff:", error);
      });
  };

  const handleMainStaffChangeForAdditionalDate = (dateIndex, type, value) => {
    const newAdditionalDates = [...additionalDates];
    newAdditionalDates[dateIndex][type] = value;
    setAdditionalDates(newAdditionalDates);

    updateDoc(doc(db, "JobData", jobData.id), { additionalDates: newAdditionalDates })
      .then(() => {
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      })
      .catch((error) => {
        console.error("Error updating additional date staff:", error);
      });
  };

  const StaffSection = ({
    title,
    mainValue,
    mainOnChange,
    fieldName,
    additionalStaff,
    type,
    dateIndex = -1,
    date = null,
  }) => (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Box sx={{ flexGrow: 1 }}>
          <StaffSelector
            label={title}
            value={mainValue}
            onChange={dateIndex === -1 ? mainOnChange : () => {}}
            customChangeHandler={
              dateIndex !== -1
                ? (value) =>
                    handleMainStaffChangeForAdditionalDate(
                      dateIndex,
                      type.toLowerCase(),
                      value
                    )
                : null
            }
            jobData={jobData}
            fieldName={fieldName}
            setHasUnsavedChanges={setHasUnsavedChanges}
            assignedStaff={assignedStaff}
          />
        </Box>
        <div style={{ flexShrink: 0 }}>
          <IconButton
            onClick={() =>
              addAdditionalStaff(
                dateIndex === -1 ? type : `additional${title}s`,
                dateIndex
              )
            }
            size="small"
            sx={{
              ml: 1,
              backgroundColor:
                theme === "dark"
                  ? "rgba(60,60,60,0.8)"
                  : "rgba(235,235,235,0.9)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 5px rgba(0,0,0,0.2)"
                  : "0 2px 5px rgba(0,0,0,0.05)",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(80,80,80,0.9)"
                    : "rgba(220,220,220,0.9)",
              },
            }}
            aria-label={`Add ${title}`}
          >
            <AddIcon />
          </IconButton>
        </div>
      </Box>
      {Array.isArray(additionalStaff) &&
        additionalStaff.map((staffId, index) => (
          <Box
            key={`${type}-${index}-${dateIndex}`}
            sx={{ display: "flex", alignItems: "center", mt: 2 }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <StaffSelector
                label={`Additional ${title}`}
                value={staffId}
                onChange={() => {}}
                customChangeHandler={(value) =>
                  handleAdditionalStaffChange(
                    dateIndex === -1 ? type : `additional${title}s`,
                    index,
                    value,
                    dateIndex
                  )
                }
                jobData={jobData}
                fieldName={`additional${title}s`}
                setHasUnsavedChanges={setHasUnsavedChanges}
                assignedStaff={assignedStaff}
              />
            </Box>
            <div style={{ flexShrink: 0 }}>
              <IconButton
                onClick={() =>
                  removeAdditionalStaff(
                    dateIndex === -1 ? type : `additional${title}s`,
                    index,
                    dateIndex
                  )
                }
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(60,60,60,0.8)"
                      : "rgba(235,235,235,0.9)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 5px rgba(0,0,0,0.2)"
                      : "0 2px 5px rgba(0,0,0,0.05)",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(80,80,80,0.9)"
                        : "rgba(220,220,220,0.9)",
                  },
                }}
                aria-label={`Remove ${title}`}
              >
                <RemoveIcon />
              </IconButton>
            </div>
          </Box>
        ))}
    </Box>
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dayjs(dateString).format("ddd, MMM D, YYYY");
  };

  const AdditionalDateStaffSection = ({ dateIndex, date }) => {
    if (!additionalDates[dateIndex].photographer)
      additionalDates[dateIndex].photographer = "";
    if (!additionalDates[dateIndex].operator)
      additionalDates[dateIndex].operator = "";
    if (!additionalDates[dateIndex].stylist)
      additionalDates[dateIndex].stylist = "";
    if (!additionalDates[dateIndex].additionalPhotographers)
      additionalDates[dateIndex].additionalPhotographers = [];
    if (!additionalDates[dateIndex].additionalOperators)
      additionalDates[dateIndex].additionalOperators = [];
    if (!additionalDates[dateIndex].additionalStylists)
      additionalDates[dateIndex].additionalStylists = [];

    return (
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 2,
            color:
              theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
            textAlign: "center",
            backgroundColor:
              theme === "dark" ? "rgba(50,50,50,0.7)" : "rgba(245,245,245,0.9)",
            borderRadius: "5px",
            py: 1,
            boxShadow:
              theme === "dark"
                ? "0 2px 5px rgba(0,0,0,0.2)"
                : "0 2px 5px rgba(0,0,0,0.05)",
          }}
        >
          {formatDate(date.start)} to {formatDate(date.end)}
        </Typography>
        <StaffSection
          title="Photographer"
          mainValue={additionalDates[dateIndex].photographer}
          mainOnChange={() => {}}
          fieldName="photographer"
          additionalStaff={additionalDates[dateIndex].additionalPhotographers}
          type="Photographer"
          dateIndex={dateIndex}
          date={date}
        />
        <StaffSection
          title="Operator"
          mainValue={additionalDates[dateIndex].operator}
          mainOnChange={() => {}}
          fieldName="operator"
          additionalStaff={additionalDates[dateIndex].additionalOperators}
          type="Operator"
          dateIndex={dateIndex}
          date={date}
        />
        <StaffSection
          title="Stylist"
          mainValue={additionalDates[dateIndex].stylist}
          mainOnChange={() => {}}
          fieldName="stylist"
          additionalStaff={additionalDates[dateIndex].additionalStylists}
          type="Stylist"
          dateIndex={dateIndex}
          date={date}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        borderRadius: "10px",
        backgroundColor:
          theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          theme === "dark"
            ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
        padding: 2,
        mt: 2,
      }}
    >
      {isSpecialEvent ? (
        // Special Event: Only show photographer and additionalPhotographers as "Staff"
        <StaffSection
          title="Staff"
          mainValue={photographer}
          mainOnChange={setPhotographer}
          fieldName="photographer"
          additionalStaff={additionalPhotographers}
          type="photographer"
        />
      ) : (
        // Regular Event: Show all staff sections as before
        <>
          {jobData.additionalDates && jobData.additionalDates.length > 0 && (
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color:
                  theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
                textAlign: "center",
                backgroundColor:
                  theme === "dark" ? "rgba(50,50,50,0.7)" : "rgba(245,245,245,0.9)",
                borderRadius: "5px",
                py: 1,
                boxShadow:
                  theme === "dark"
                    ? "0 2px 5px rgba(0,0,0,0.2)"
                    : "0 2px 5px rgba(0,0,0,0.05)",
              }}
            >
              {formatDate(jobData.bookedStart)} to{" "}
              {formatDate(jobData.bookedFinish)}
            </Typography>
          )}
          <StaffSection
            title="Photographer"
            mainValue={photographer}
            mainOnChange={setPhotographer}
            fieldName="photographer"
            additionalStaff={additionalPhotographers}
            type="photographer"
          />
          <StaffSection
            title="Operator"
            mainValue={operator}
            mainOnChange={setOperator}
            fieldName="operator"
            additionalStaff={additionalOperators}
            type="operator"
          />
          <StaffSection
            title="Stylist"
            mainValue={stylist}
            mainOnChange={setStylist}
            fieldName="stylist"
            additionalStaff={additionalStylists}
            type="stylist"
          />
          {additionalDates &&
            additionalDates.length > 0 &&
            additionalDates.map((date, index) => (
              <AdditionalDateStaffSection
                key={`date-${index}`}
                dateIndex={index}
                date={date}
              />
            ))}
        </>
      )}
    </Box>
  );
};

export default StaffContainer;
